import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AccessibilityControls } from "@/components/a11y/AccessibilityControls";
import { LanguageToggle } from "@/components/i18n/LanguageToggle";
import { useAuthStore } from "@/state/useAuthStore";
import { logout } from "@/api/auth";
import { cn } from "@/lib/utils";
import { registrationEnabled } from "@/config/features";

// Hysteresis for the compact-header toggle: turn ON only past 88px and back OFF
// only below 48px. The gap absorbs the ~16px reflow when the header resizes,
// breaking the scroll → resize → scroll feedback loop that made the navbar
// "shake" when the skip link parks the scroll near the threshold.
const SCROLL_ON = 88;
const SCROLL_OFF = 48;

// Temporarily hidden until the Bahasa Malaysia copy is signed off.
// Flip to true to restore the EN/BM toggle (i18n + translations stay intact).
const SHOW_LANGUAGE_TOGGLE = false;

// Trailing slashes are load-bearing, not cosmetic: every canonical (via
// absoluteUrl("/race-details/")) and every sitemap.xml entry uses the slash form.
// The CloudFront function rewrites rather than redirects, so the slashless form
// also returns 200 — meaning links without the slash make Google crawl a URL that
// then points its canonical elsewhere. That halves internal link value on a site
// with almost no external links to spare. Keep these in step with sitemap.xml.
const navItems: ReadonlyArray<{ to: string; labelKey: string; end?: boolean }> = [
  { to: "/", labelKey: "nav.home", end: true },
  { to: "/race-details/", labelKey: "nav.raceDay" },
  { to: "/registration", labelKey: "nav.registration" },
  { to: "/sponsorship/", labelKey: "nav.sponsorship" },
  { to: "/sponsors/", labelKey: "nav.sponsors" },
  { to: "/contact/", labelKey: "nav.contact" }
];

// Registration is gated off for the marketing-only launch (no backend). Drop its
// nav tab in that build; the auth cluster (Log In / dashboard) is hidden too below.
const visibleNavItems = registrationEnabled
  ? navItems
  : navItems.filter((item) => item.to !== "/registration");

export function Header() {
  const [open, setOpen] = useState(false);
  // Once the user scrolls past the hero, the header tightens: shorter height,
  // stronger backdrop blur, sharper border. Passive listener; one boolean flip
  // per crossing keeps React re-renders to the bare minimum.
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation("common");
  const { accessToken, user, clearAuth } = useAuthStore();

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled((prev) => (prev ? y > SCROLL_OFF : y > SCROLL_ON));
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  async function handleLogout() {
    try {
      await logout();
    } catch {
      // ignore — clear local state regardless
    }
    clearAuth();
    navigate("/");
  }

  return (
    <header
      className={cn(
        // Standard translucent light bar on every route (the hero key visual
        // starts below the header, so there is no transparent-overlay state).
        "sticky top-0 z-40 w-full border-b transition-[background-color,backdrop-filter,border-color] duration-200",
        scrolled
          ? "border-primary/15 bg-background/95 backdrop-blur-md"
          : "border-border/60 bg-background/85 backdrop-blur supports-[backdrop-filter]:bg-background/70"
      )}
    >
      <div
        className={cn(
          "container flex items-center justify-between transition-[height] duration-200",
          scrolled ? "h-16" : "h-20"
        )}
      >
        <Link to="/" className="flex shrink-0 items-center">
          <img
            src="/BBCxTEKLRR2026_Logo_NoSlogan.png"
            alt="Bursa Bull Charge × The Edge KL Rat Race 2026"
            width={3335}
            height={808}
            // Explicit intrinsic size + aspect-ratio: without these, Safari's
            // flexbox img bug renders this wide banner at (near) its natural
            // size instead of honoring the CSS height, blowing up the header.
            className={cn(
              // Slightly smaller at lg so the full nav + auth cluster fit within
              // the container at 1024px; back to full size from xl. Stepped down
              // again when the Sponsors tab took the count to six links.
              "aspect-[3335/808] w-auto shrink-0 transition-[height] duration-200",
              scrolled ? "h-9 xl:h-11" : "h-11 xl:h-14"
            )}
          />
        </Link>

        {/* Full nav only from lg — at md (tablet portrait) the logo + links +
            auth cluster overflow the viewport, pushing the page wider than the
            hero and leaving a blank strip (seen on iPad Air). Tablets use the
            hamburger menu; lg gets tighter link spacing, xl full.

            The budget at lg is tight: 1024px viewport = 928px of container, and
            six links (Home / Race Details / Registration / Sponsorship /
            Sponsors / Contact) plus the logo and auth cluster need ~890px with
            the reductions below (13px type, normal tracking, gap-3, smaller
            logo). If a seventh tab is ever added, hide Home from the desktop
            row — the logo already links there — rather than pushing the whole
            nav to xl, which would demote every 1024–1279px laptop and
            iPad-landscape user to the hamburger. */}
        <nav className="hidden lg:flex items-center gap-3 xl:gap-6 2xl:gap-8">
          {visibleNavItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                cn(
                  "whitespace-nowrap text-[13px] xl:text-sm font-medium uppercase tracking-normal xl:tracking-wide transition-colors",
                  isActive ? "text-primary" : "text-foreground/70 hover:text-primary"
                )
              }
            >
              {t(item.labelKey)}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          {/* Language + accessibility controls — reachable on every page, all breakpoints. */}
          {SHOW_LANGUAGE_TOGGLE && <LanguageToggle />}
          <AccessibilityControls />

          {registrationEnabled && (
            <div className="hidden lg:flex items-center gap-3">
              {accessToken && user ? (
                <>
                  <Link to="/dashboard">
                    <Button variant="ghost" size="sm" className="max-w-[8rem] hover:bg-primary/10 hover:text-primary"><span className="truncate">{user.name.split(" ")[0]}</span></Button>
                  </Link>
                  <Button variant="outline" size="sm" onClick={handleLogout}>
                    <LogOut className="h-4 w-4" /> {t("header.signOut")}
                  </Button>
                </>
              ) : (
                <Link to="/login"><Button variant="ghost" size="sm" className="hover:bg-transparent hover:text-primary">{t("header.logIn")}</Button></Link>
              )}
            </div>
          )}

          <button
            type="button"
            className="lg:hidden inline-flex h-11 w-11 items-center justify-center rounded-md border border-border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            onClick={() => setOpen((s) => !s)}
            aria-label={open ? t("header.closeMenu") : t("header.openMenu")}
            aria-expanded={open}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-border bg-background">
          <div className="container py-4 flex flex-col gap-2">
            {visibleNavItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  cn(
                    "px-3 py-2 rounded-md text-sm font-medium uppercase tracking-wide",
                    isActive ? "bg-primary/10 text-primary" : "text-foreground/80 hover:bg-accent"
                  )
                }
              >
                {t(item.labelKey)}
              </NavLink>
            ))}
            {registrationEnabled && (
              <>
                <div className="border-t border-border my-2" />
                {accessToken && user ? (
                  <>
                    <Link to="/dashboard" onClick={() => setOpen(false)} className="px-3 py-2 text-sm">{t("header.dashboard")}</Link>
                    <button
                      type="button"
                      onClick={() => { setOpen(false); void handleLogout(); }}
                      className="px-3 py-2 text-left text-sm flex items-center gap-2"
                    >
                      <LogOut className="h-4 w-4" /> {t("header.signOut")}
                    </button>
                  </>
                ) : (
                  <Link to="/login" onClick={() => setOpen(false)} className="px-3 py-2 text-sm">{t("header.logIn")}</Link>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
